import { useState, useEffect, useCallback, RefObject, useRef } from "react";
import { useWindowResize } from "helpers/utility/utilities";

export default function useImageDragAndZoom(
  maxZoom: number,
  loading: boolean,
  imageContainerRef: RefObject<HTMLElement | null>,
  minZoom: number,
  draggable = true,
) {
  const [zoomLevel, setZoomLevel] = useState(minZoom);
  const [isDragging, setIsDragging] = useState(false);
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });
  const [translatePosition, setTranslatePosition] = useState({ x: 0, y: 0 });
  const [isImageCentered, setIsImageCentered] = useState(true);
  const [newDimensions, setNewDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [isZooming, setIsZooming] = useState(false);
  const zoomTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { height: windowHeight, width: windowWidth } = useWindowResize();

  const setZoomingState = useCallback(() => {
    if (zoomTimeoutRef.current) {
      clearTimeout(zoomTimeoutRef.current);
    }

    setIsZooming(true);

    zoomTimeoutRef.current = setTimeout(() => {
      setIsZooming(false);
    }, 150);
  }, []);

  const onMouseDown = useCallback(
    (e: MouseEvent) => {
      if (imageContainerRef.current?.contains(e.target as Node)) {
        setIsDragging(true);
        setStartPosition({
          x: e.clientX - translatePosition.x,
          y: e.clientY - translatePosition.y,
        });
      }
    },
    [translatePosition, imageContainerRef],
  );

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        setTranslatePosition({
          x: e.clientX - startPosition.x,
          y: e.clientY - startPosition.y,
        });
      }
    },
    [isDragging, startPosition],
  );

  const onMouseUp = () => setIsDragging(false);

  const centerImageHandler = () => {
    setTranslatePosition({ x: 0, y: 0 });
  };

  const applyZoom = (
    newZoom: number,
    origin: "center" | "pointer",
    pointer?: { x: number; y: number },
  ) => {
    if (!imageContainerRef.current) return;

    // Only set zooming state if we're actually changing zoom level
    if (newZoom !== zoomLevel) {
      setZoomingState();
    }

    const container = imageContainerRef.current;
    const scaleFactor = newZoom / zoomLevel;

    let originX = 0;
    let originY = 0;

    if (origin === "pointer" && pointer) {
      const rect = container.getBoundingClientRect();
      originX = pointer.x - rect.left;
      originY = pointer.y - rect.top;
    } else if (origin === "center") {
      const parentEl = container.parentElement;
      if (!parentEl) return;

      const parentRect = parentEl.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      // Calculate the center of the parent relative to the container
      originX = parentRect.left + parentRect.width / 2 - containerRect.left;
      originY = parentRect.top + parentRect.height / 2 - containerRect.top;
    }

    setTranslatePosition((prev) => ({
      x: prev.x - originX * (scaleFactor - 1),
      y: prev.y - originY * (scaleFactor - 1),
    }));

    setZoomLevel(newZoom);
  };

  const zoomIn = () => {
    const newZoom = Math.min(zoomLevel + 10, maxZoom);
    applyZoom(newZoom, "center");
  };

  const zoomOut = () => {
    const newZoom = Math.max(zoomLevel - 10, 100);
    applyZoom(newZoom, "center");
  };

  const setZoomTo = (value: number) => {
    const newZoom = Math.max(100, Math.min(value, maxZoom));
    applyZoom(newZoom, "center");
  };

  useEffect(() => {
    setIsImageCentered(translatePosition.x === 0 && translatePosition.y === 0);
  }, [translatePosition, zoomLevel]);

  useEffect(() => {
    if (!draggable) {
      return;
    }
    document.addEventListener("mousedown", onMouseDown);
    if (isDragging) {
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    } else {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    }

    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [draggable, isDragging, onMouseDown, onMouseMove]);

  useEffect(() => {
    if (zoomLevel === 100 && !draggable) {
      setTranslatePosition({ x: 0, y: 0 });
    }
  }, [zoomLevel, draggable]);

  useEffect(() => {
    if (imageContainerRef.current) {
      const el = imageContainerRef.current;
      el.style.cursor = isDragging || zoomLevel > 100 ? "grab" : "auto";
      el.style.transform = `translate(${translatePosition.x}px, ${
        translatePosition.y
      }px) scale(${zoomLevel / 100})`;

      const rect = el.getBoundingClientRect();
      setNewDimensions({ width: rect.width, height: rect.height });
    }
    //eslint-disable-next-line
  }, [
    translatePosition,
    isDragging,
    zoomLevel,
    imageContainerRef,
    loading,
    windowHeight,
    windowWidth,
  ]);

  useEffect(() => {
    const container = imageContainerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const step = e.deltaY > 0 ? -10 : 10;
      const newZoom = Math.max(100, Math.min(maxZoom, zoomLevel + step));

      applyZoom(newZoom, "pointer", { x: e.clientX, y: e.clientY });
    };

    container.addEventListener("wheel", handleWheel, { passive: false });
    return () => container.removeEventListener("wheel", handleWheel);
    //eslint-disable-next-line
  }, [zoomLevel, maxZoom]);

  //prevent default (browser) zoom
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      const container = imageContainerRef.current;
      if (!container) return;

      // Only zoom when Ctrl is held AND cursor is over image container
      if ((e.ctrlKey || e.metaKey) && container.contains(e.target as Node)) {
        e.preventDefault();
      }
    };

    const handleCtrlZoomKeys = (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;

      const key = e.key;

      if (key === "+" || key === "=" || key === "-") {
        e.preventDefault();

        const step = key === "-" ? -10 : 10;
        const newZoom = Math.max(100, Math.min(maxZoom, zoomLevel + step));

        applyZoom(newZoom, "center");
      }

      if (key === "0") {
        e.preventDefault();
        if (zoomLevel !== 100) {
          applyZoom(100, "center");
          centerImageHandler?.(); // Optional: reset position too
        }
      }
    };

    window.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("keydown", handleCtrlZoomKeys);

    return () => {
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("keydown", handleCtrlZoomKeys);
    };
    //eslint-disable-next-line
  }, [zoomLevel, maxZoom]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (zoomTimeoutRef.current) clearTimeout(zoomTimeoutRef.current);
    };
  }, []);

  return {
    isZooming,
    newDimensions,
    isImageCentered,
    translatePosition,
    zoomIn,
    zoomOut,
    setZoomTo,
    centerImageHandler,
    imageZoomInLevel: zoomLevel,
    setImageZoomInLevel: setZoomLevel,
  };
}
